<template>
  <div id="endDevicesAlarms">
    <card id="endDevicesAlarms-clusters" :title="$t('al-endDevices:clustersOverview')" :title-small="$t('al-endDevices:selectCluster')" :visible="true" class="alarms-groups" collapsible no-footer>
      <template slot="card-body">
        <div v-for="(customer, index) in lastLnsEventsCounters.customers"
             :key="customer.customer.id"
             :class="{'mb-3': index < lastLnsEventsCounters.customers.length-1}"
             class="row mx-0"
        >
          <h4 class="w-100">
            <span>{{ customer.customer.name | capitalize }}</span>
            <small :class="customer.clusterAlarms > 0 ? 'text-danger' : 'text-success'" class="ml-3">{{ $t('al-gateways:alarmsCount', {count: customer.clusterAlarms}) }}</small>
          </h4>

          <div v-for="cluster in customer.clusters"
               :key="cluster.cluster.id"
               :class="{selected: cluster.cluster.id === selectedClusterId}"
               class="col-6 col-lg-3 col-xl-2 px-3 py-2 alarm-group"
               @click.prevent="selectedCluster = cluster.cluster"
          >
            <div class="text-truncate">{{ cluster.cluster.name }}</div>
            <span :class="cluster.alarms > 0 ? 'badge-danger' : 'badge-success'" class="badge badge-pill ml-1">{{ cluster.alarms }}</span>
          </div>
        </div>
      </template>
    </card>

    <card id="endDevicesAlarms-filters" :title="$t('al-endDevices:filter')" :visible="false" collapsible>
      <template slot="card-body">
        <div class="form-row">
          <div class="form-group col-lg-6">
            <label for="name" class="control-label">{{ $t('al-endDevices:type') | capitalize }}</label>
            <select id="name" v-model="filterName" class="form-control">
              <option value="" />
              <option v-for="type in endDeviceAlarmTypes" :key="type.value" :value="type.value">{{ type.text }}</option>
            </select>
          </div>

          <div class="form-group col-lg-6">
            <label for="status" class="control-label">{{ $t('al-endDevices:status') | capitalize }}</label>
            <select id="status" v-model="filterStatus" class="form-control">
              <option value="UNREAD">{{ $t('al-endDevices:unread') | capitalize }}</option>
              <option value="READ">{{ $t('al-endDevices:read') | capitalize }}</option>
              <option value="ALL">{{ $t('al-endDevices:all') | capitalize }}</option>
            </select>
          </div>
        </div>

        <div class="form-row">
          <div class="form-group col-md-6">
            <label for="period" class="control-label">{{ $t('al-endDevices:period') | capitalize }}</label>
            <div class="input-group">
              <flat-pickr id="period"
                          v-model="filterPeriod"
                          :placeholder="$t('al-endDevices:periodPlaceholder') | capitalize"
                          :config="fpConfig"
                          class="form-control"
              />
              <span class="input-group-append">
                <button id="resetPeriodButton" class="btn btn-flat btn-sm" type="button" @click="filterPeriod=null"><i class="icon-remove" /></button>
              </span>
            </div>
          </div>
        </div>
      </template>

      <template slot="card-footer">
        <button id="filterButton" class="btn btn-primary" @click="filter()">{{ $t('al-endDevices:search') | capitalize }}</button>
      </template>
    </card>

    <grid id="endDevicesAlarmsGrid"
          :title="$t('al-endDevices:endDevicesAlarms')"
          :columns="columns"
          :paginated-dto="rows"
          :download-btn="false"
          class="alarms-table"
          @load="filter"
    >
      <span slot="status" slot-scope="props" :class="{unread: !props.dto.read}" />

      <div slot="date" slot-scope="props" class="d-flex justify-content-between">
        <span>{{ props.dto.date | date }}</span>
        <i v-if="props.dto.read" :title="$t('al-endDevices:markAsUnread') | capitalize" class="icon-alarm-undo" @click.prevent="markAsUnread(props.dto)" />
        <i v-else :title="$t('al-endDevices:markAsRead') | capitalize" class="icon-alarm-check" @click.prevent="markAsRead(props.dto)" />
      </div>

      <router-link slot="endDevice" slot-scope="props" :to="{name: 'endDevice', params: {devEui: props.dto.eui}}">{{ props.dto.eui }}</router-link>
    </grid>
  </div>
</template>

<script>
import { filter, isEmpty, each } from 'lodash-es'
import { mapGetters } from 'vuex'
import { endDeviceAlarmTypes } from '@/configuration/alarmTypes'
import to from '@/modules/await-to-js'

export default {
  titleKey: 'alarmsEndDevices',

  data () {
    return {
      // Selected cluster
      selectedCluster: null,
      // Filters
      filterName: '',
      filterStatus: 'UNREAD',
      filterPeriod: '',
      // Display events
      rows: {},

      // In data to be modified if needed
      fpConfig: {
        wrap: true,
        mode: 'range',
        enableTime: true,
        altInput: true,
        altFormat: 'Y/m/d H:i',
        dateFormat: 'U',
        time_24hr: true
      }
    }
  },

  computed: {
    ...mapGetters(['lastLnsEventsCounters']),

    endDeviceAlarmTypes () { return filter(endDeviceAlarmTypes, ['type', 'alarm']) },

    selectedClusterId () {
      // When selecting a cluster
      if (this.selectedCluster) {
        return this.selectedCluster.id
      }

      return null
    },

    columns () {
      return [
        { slot: 'status', headerClass: 'alarm-status', class: 'alarm-status' },
        { text: this.$t('al-endDevices:date'), slot: 'date', sort: 'date' },
        { text: this.$t('al-endDevices:type'), id: 'name' },
        { text: this.$t('al-endDevices:value'), id: 'value' },
        { text: this.$t('al-endDevices:endDevice'), slot: 'endDevice' }
      ]
    }
  },

  watch: {
    selectedClusterId () {
      this.filter()
    }
  },

  created () {
    this.$store.dispatch('getLastLnsEventsCounters')
      .then(() => {
        // If a selectedClusterId is in store, select it
        if (this.$store.getters.selectedClusterId && !isEmpty(this.lastLnsEventsCounters.customers)) {
          each(this.lastLnsEventsCounters.customers, customer => {
            each(customer.clusters, cluster => {
              if (cluster.cluster.id === this.$store.getters.selectedClusterId) this.selectedCluster = cluster.cluster
            })
          })
        }

        // When no cluster found or selected and there are clusters, select the first cluster of the first customer
        if (!this.selectedCluster && !isEmpty(this.lastLnsEventsCounters.customers) && this.lastLnsEventsCounters.customers[0].clusters.length > 0) {
          this.selectedCluster = this.lastLnsEventsCounters.customers[0].clusters[0].cluster
        }

        this.filter()

        // Scroll to selected cluster
        this.$nextTick(() => {
          const clustersList = document.querySelectorAll('#endDevicesAlarms-clusters .card-body')[0]
          const selectedCluster = document.querySelectorAll('#endDevicesAlarms-clusters .alarm-group.selected')[0]
          if (clustersList && selectedCluster) clustersList.scrollTop = selectedCluster.parentElement.offsetTop - clustersList.offsetTop
        })
      })
  },

  methods: {
    filter (options = {}) {
      if (!this.selectedClusterId) return

      let search = []

      // Add criteria
      if (this.filterName) {
        search = this.$utils.addSearch(search, 'eq', this.filterName, 'name')
      }

      if (this.filterStatus !== 'ALL') {
        search = this.$utils.addSearch(search, 'eq', '' + (this.filterStatus === 'READ'), 'read')
      }

      // Flatpickr return a string "<from> to <to>" with <from> and <to> dates in seconds
      const filterPeriod = this.filterPeriod ? this.filterPeriod.split(' to ').map(v => v * 1000) : []
      if (!isEmpty(filterPeriod)) {
        search = this.$utils.addSearch(search, 'gte', filterPeriod[0], 'date')
        search = this.$utils.addSearch(search, 'lte', filterPeriod[1], 'date')
      }

      // Search for alarms only, not notifications
      search = this.$utils.addSearch(search, 'eq', 'ALARM', 'type')

      // Format criteria
      if (search.length > 0) {
        options.search = JSON.stringify(search.length === 1 ? search[0] : this.$utils.formatSearch('and', search))
      }

      // Get filtered data
      this.getAlarms(options)
    },

    async getAlarms (options) {
      const params = this.$utils.getParams({ sort: '-date' }, options, this.queryPageParams)
      let error, response
      [error, response] = await to(this.$api.getClusterLastEvents(this.selectedCluster, params))
      if (error || !response) return

      this.rows = response.data
    },

    // Mark alarm as read
    async markAsRead (alarm) {
      let error
      [error] = await to(this.$api.patchLastEvent(alarm, { read: true }))
      if (error) return

      this.$store.dispatch('getLastLnsEventsCounters')
      this.filter()
    },

    // Mark alarm as unread
    async markAsUnread (alarm) {
      let error
      [error] = await to(this.$api.patchLastEvent(alarm, { read: false }))
      if (error) return

      this.$store.dispatch('getLastLnsEventsCounters')
      this.filter()
    }
  }
}
</script>
